import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

interface ISO14001State {
  enabled: "enabled" | "disabled";
}

const defaultISO14001: ISO14001State = {
  enabled: "disabled",
};

export default function ISO14001Module() {
  const { activeTenancy } = useTenancy();
  const [iso14001, setIso14001] = useState<ISO14001State>(defaultISO14001);
  const [iso14001Initial, setIso14001Initial] = useState<ISO14001State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchISO14001 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const iso14001Settings = settings?.["iso14001"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof iso14001Settings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(iso14001Settings.Enabled);
        } else if (typeof iso14001Settings.enabled !== 'undefined') {
          isEnabled = checkEnabled(iso14001Settings.enabled);
        } else if (typeof iso14001Settings.active !== 'undefined') {
          isEnabled = checkEnabled(iso14001Settings.active);
        } else if (typeof iso14001Settings.Active !== 'undefined') {
          isEnabled = checkEnabled(iso14001Settings.Active);
        }
        setIso14001({ enabled: isEnabled ? "enabled" : "disabled" });
        setIso14001Initial({ enabled: isEnabled ? "enabled" : "disabled" });
      } catch (err) {
        toast.error("Failed to fetch ISO 14001 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchISO14001();
  }, []);

  const iso14001Changed = iso14001Initial && JSON.stringify(iso14001) !== JSON.stringify(iso14001Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = iso14001Initial?.enabled === "disabled";
      const isNowEnabled = iso14001.enabled === "enabled";
      
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        iso14001: { Enabled: iso14001.enabled === "enabled" },
      });
      
      // DED-1511: If ISO 14001 is being enabled (was disabled, now enabled), copy tasks
      if (wasDisabled && isNowEnabled && activeTenancy?.id) {
        try {
          const customerId = activeTenancy.id;
          
          toast.info("Copying ISO 14001 tasks...");
          
          await copyTasksToCustomerAssurance(
            customerId,
            'ISO14001',
            undefined,
            undefined
          );
          
          toast.success("ISO 14001 settings saved and tasks copied");
        } catch (taskError) {
          console.error('Error copying ISO 14001 tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
        }
      } else {
        toast.success("ISO 14001 settings saved");
      }
      
      setIso14001Initial({ ...iso14001 });
    } catch (err) {
      toast.error("Failed to save ISO 14001 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="ISO 14001">🌱</span>
        </div>
        <div>
          <div className="font-semibold text-lg">ISO 14001</div>
          <div className="text-xs text-muted-foreground">Environmental Management System Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={iso14001.enabled}
          onValueChange={val => setIso14001(v => ({ ...v, enabled: val as ISO14001State["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!iso14001Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save ISO 14001 Settings"}
        </Button>
      </div>
    </div>
  );
}
